import React, { useState, useEffect, useMemo } from 'react';
import {
	Card,
	CardContent,
	Typography,
	Button,
	CircularProgress,
	Alert,
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	IconButton,
	Tooltip,
	TextField,
	TablePagination,
	Grid,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { Title, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Tooltip as ChartTooltip,
	Legend,
} from 'chart.js';
import {
	MCPSession,
	MCPStats,
	getMCPSessions,
	getMCPStats,
} from '../../Services/quantiAI';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

export const AIConversationsList = () => {
	const [sessions, setSessions] = useState<MCPSession[]>([]);
	const [stats, setStats] = useState<MCPStats | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const [projectFilter, setProjectFilter] = useState('');
	const [userFilter, setUserFilter] = useState<{ id: number; name: string } | null>(null);
	const notify = useNotify();
	const navigate = useNavigate();

	const loadData = async () => {
		setLoading(true);
		setError(null);
		try {
			const [sessionsRes, statsRes] = await Promise.all([
				getMCPSessions({
					limit: rowsPerPage,
					offset: page * rowsPerPage,
					project_folder_id: projectFilter || undefined,
					user_id: userFilter?.id || undefined,
				}),
				getMCPStats({
					project_folder_id: projectFilter || undefined,
					user_id: userFilter?.id || undefined,
				}),
			]);
			setSessions(sessionsRes.sessions || []);
			setTotal(sessionsRes.total || 0);
			setStats(statsRes);
		} catch (err: any) {
			setError(err.message || 'Erreur lors du chargement');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadData();
	}, [page, rowsPerPage, userFilter]);

	const handleRefresh = () => {
		loadData();
		notify('Données rafraîchies', { type: 'info' });
	};

	const handleView = (id: string) => {
		navigate(`/ai-conversations/${id}`);
	};

	const handleChangePage = (_: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setProjectFilter(e.target.value);
	};

	const handleFilterSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setPage(0);
		loadData();
	};

	const handleUserFilter = (userId: number, userName: string) => {
		setUserFilter({ id: userId, name: userName });
		setPage(0);
	};

	const handleClearUserFilter = () => {
		setUserFilter(null);
		setPage(0);
	};

	const formatDuration = (ms: number) => {
		if (ms < 1000) return `${ms}ms`;
		if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
		return `${(ms / 60000).toFixed(1)}min`;
	};

	const dailyChartData = useMemo(() => {
		if (!stats?.daily_stats?.length) return null;
		const sorted = [...stats.daily_stats].sort((a, b) => a.date.localeCompare(b.date));
		return {
			labels: sorted.map((d) =>
				new Date(d.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
			),
			datasets: [
				{
					label: 'Sessions',
					data: sorted.map((d) => d.sessions),
					backgroundColor: 'rgba(25, 118, 210, 0.7)',
				},
				{
					label: 'Messages',
					data: sorted.map((d) => d.messages),
					backgroundColor: 'rgba(156, 39, 176, 0.7)',
				},
			],
		};
	}, [stats]);

	const dailyChartOptions = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			plugins: {
				legend: { position: 'top' as const },
			},
			scales: {
				x: { grid: { display: false } },
				y: { beginAtZero: true, ticks: { precision: 0 } },
			},
		}),
		[]
	);

	const formatDate = (dateStr: string) => {
		const date = new Date(dateStr);
		return date.toLocaleString('fr-FR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	return (
		<Card>
			<Title title="Quanti AI - Conversations MCP" />
			<CardContent>
				{/* Stats cards */}
				{stats && (
					<Grid container spacing={2} sx={{ mb: 3 }}>
						<Grid item xs={12} sm={6} md={2}>
							<Paper sx={{ p: 2, textAlign: 'center' }}>
								<Typography variant="h4">{stats.total_sessions}</Typography>
								<Typography variant="body2" color="text.secondary">
									Sessions
								</Typography>
							</Paper>
						</Grid>
						<Grid item xs={12} sm={6} md={2}>
							<Paper sx={{ p: 2, textAlign: 'center' }}>
								<Typography variant="h4">{stats.total_messages}</Typography>
								<Typography variant="body2" color="text.secondary">
									Messages
								</Typography>
							</Paper>
						</Grid>
						<Grid item xs={12} sm={6} md={2}>
							<Paper sx={{ p: 2, textAlign: 'center' }}>
								<Typography variant="h4">{stats.unique_users}</Typography>
								<Typography variant="body2" color="text.secondary">
									Utilisateurs
								</Typography>
							</Paper>
						</Grid>
						<Grid item xs={12} sm={6} md={2}>
							<Paper sx={{ p: 2, textAlign: 'center' }}>
								<Typography variant="h4" color="success.main">
									{stats.positive_feedback}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									<ThumbUpIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
									Positifs
								</Typography>
							</Paper>
						</Grid>
						<Grid item xs={12} sm={6} md={2}>
							<Paper sx={{ p: 2, textAlign: 'center' }}>
								<Typography variant="h4" color="error.main">
									{stats.negative_feedback}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									<ThumbDownIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
									Négatifs
								</Typography>
							</Paper>
						</Grid>
						<Grid item xs={12} sm={6} md={2}>
							<Paper sx={{ p: 2, textAlign: 'center' }}>
								<Typography variant="h4">
									{stats.avg_messages_per_session.toFixed(1)}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									Msg/Session
								</Typography>
							</Paper>
						</Grid>
					</Grid>
				)}

				{/* Daily chart */}
				{dailyChartData && (
					<Paper sx={{ p: 2, mb: 3, height: 260 }}>
						<Typography variant="subtitle2" gutterBottom>
							Activité des 30 derniers jours
						</Typography>
						<Box sx={{ height: 220 }}>
							<Bar data={dailyChartData} options={dailyChartOptions} />
						</Box>
					</Paper>
				)}

				{/* Actions */}
				<Box
					sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}
				>
					<Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
						<Box component="form" onSubmit={handleFilterSubmit} sx={{ display: 'flex', gap: 1 }}>
							<TextField
								size="small"
								label="Project Folder ID"
								value={projectFilter}
								onChange={handleFilterChange}
								placeholder="ex: p57d4af1b"
							/>
							<Button type="submit" variant="outlined" size="small">
								Filtrer
							</Button>
						</Box>
						{userFilter && (
							<Chip
								label={`Utilisateur : ${userFilter.name}`}
								onDelete={handleClearUserFilter}
								deleteIcon={<ClearIcon />}
								color="primary"
								variant="outlined"
							/>
						)}
					</Box>
					<Box sx={{ display: 'flex', gap: 1 }}>
						<Button
							variant="outlined"
							onClick={() => navigate('/ai-feedback')}
						>
							Voir les feedbacks
						</Button>
						<Button
							variant="outlined"
							startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
							onClick={handleRefresh}
							disabled={loading}
						>
							Rafraîchir
						</Button>
					</Box>
				</Box>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{/* Table */}
				{loading && sessions.length === 0 ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
						<CircularProgress />
					</Box>
				) : sessions.length > 0 ? (
					<>
						<TableContainer component={Paper}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Utilisateur</TableCell>
										<TableCell>Provider</TableCell>
										<TableCell align="center">Messages</TableCell>
										<TableCell align="center">Tools</TableCell>
										<TableCell align="center">Durée</TableCell>
										<TableCell align="center">Feedback</TableCell>
										<TableCell>Dernière activité</TableCell>
										<TableCell align="right">Actions</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{sessions.map((session) => (
										<TableRow key={session.id} hover>
											<TableCell>
												<Typography
													variant="body2"
													fontWeight="medium"
													sx={{
														cursor: 'pointer',
														'&:hover': {
															color: 'primary.main',
															textDecoration: 'underline',
														},
													}}
													onClick={() =>
														handleUserFilter(
															session.user_id,
															session.user_name || session.user_email || `User #${session.user_id}`
														)
													}
												>
													{session.user_name || session.user_email || `User #${session.user_id}`}
												</Typography>
												{session.user_name && session.user_email && (
													<Typography variant="caption" color="text.secondary">
														{session.user_email}
													</Typography>
												)}
											</TableCell>
											<TableCell>
												{session.client_type ? (
													<Chip label={session.client_type} size="small" />
												) : (
													'-'
												)}
											</TableCell>
											<TableCell align="center">{session.message_count}</TableCell>
											<TableCell align="center">{session.tool_call_count}</TableCell>
											<TableCell align="center">
												{formatDuration(session.total_duration_ms)}
											</TableCell>
											<TableCell align="center">
												{session.feedback_count > 0 ? (
													<Chip
														label={session.feedback_count}
														size="small"
														color="primary"
													/>
												) : (
													'-'
												)}
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{formatDate(session.last_activity_at)}
												</Typography>
											</TableCell>
											<TableCell align="right">
												<Tooltip title="Voir le détail">
													<IconButton
														size="small"
														onClick={() => handleView(session.id)}
													>
														<VisibilityIcon />
													</IconButton>
												</Tooltip>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							component="div"
							count={total}
							page={page}
							onPageChange={handleChangePage}
							rowsPerPage={rowsPerPage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							rowsPerPageOptions={[10, 25, 50, 100]}
							labelRowsPerPage="Par page"
						/>
					</>
				) : (
					<Alert severity="info">
						Aucune session MCP enregistrée. Les conversations apparaîtront ici une fois
						que les utilisateurs commenceront à interagir avec l'IA.
					</Alert>
				)}

				{/* Top tools */}
				{stats && stats.top_tools && stats.top_tools.length > 0 && (
					<Box sx={{ mt: 4 }}>
						<Typography variant="h6" gutterBottom>
							Top Tools
						</Typography>
						<TableContainer component={Paper}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Tool</TableCell>
										<TableCell align="right">Appels</TableCell>
										<TableCell align="right">Taux de succès</TableCell>
										<TableCell align="right">Durée moyenne</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{stats.top_tools.map((tool) => (
										<TableRow key={tool.tool_name}>
											<TableCell>
												<code>{tool.tool_name}</code>
											</TableCell>
											<TableCell align="right">{tool.call_count}</TableCell>
											<TableCell align="right">
												<Chip
													label={`${tool.success_rate.toFixed(1)}%`}
													size="small"
													color={tool.success_rate >= 95 ? 'success' : tool.success_rate >= 80 ? 'warning' : 'error'}
												/>
											</TableCell>
											<TableCell align="right">
												{formatDuration(tool.avg_duration_ms)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				)}
			</CardContent>
		</Card>
	);
};
